import { InputCheckbox } from '@creditornot/cb-checkbox';
import { cbSemanticCssVars, Typography } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import debounce from 'lodash/debounce';
import React, { useRef } from 'react';
import { Field, useForm } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { RFFFieldErrorWrapper } from 'common/components';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { useCampaignModuleContext } from 'modules/campaigns/context';

import { DeliveryDiscountFormValues } from '../../../types';
import { CampaignsTargetContent } from '../../../utils/telemetry/constants';
import { telemetryUpdateCampaignInteractionClickCampaignUpdate } from '../../../utils/telemetry/telemetry';
import CampaignFormItem from '../../CampaignFormItem';
import { HelperText } from '../../HelperText';
import { UNLIMITED_TOTAL_BUDGET_SUM } from '../constants';

export type BudgetFormItemProps = {
  isDisabled?: boolean;
  recommendedBudget: number;
  'data-testid'?: string;
};

const StyledInput = styled(Input)`
  width: 175px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
  }
`;

const StyledCheckboxLabel = styled.label`
  display: flex;
  gap: 8px;
  color: ${cbSemanticCssVars.textPrimary};
`;

const StyledInputsContainer = styled.div`
  flex: 1;
  display: flex;
  gap: 16px;
  align-items: center;
  margin-block-start: 12px;
  width: 100%;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const NON_DIGITS_REGEX = /\D+/g;

export const BudgetFormItem = ({ isDisabled, recommendedBudget, ...props }: BudgetFormItemProps) => {
  const { currency } = useCampaignModuleContext();
  const intl = useIntl();
  const form = useForm<DeliveryDiscountFormValues>();
  const previousBudgetRef = useRef<number>();

  const { totalBudgetSum: totalBudgetSumValue, campaignType } = form.getState().values;
  const isUnlimitedBudget = totalBudgetSumValue === UNLIMITED_TOTAL_BUDGET_SUM;

  const handleUnlimitedBudgetCheckbox = (isChecked: boolean) => {
    if (isChecked) {
      previousBudgetRef.current = totalBudgetSumValue;
      form.change('totalBudgetSum', UNLIMITED_TOTAL_BUDGET_SUM);
    } else {
      form.change('totalBudgetSum', previousBudgetRef.current ?? recommendedBudget);
    }
  };

  const handleBudgetChangeTelemetry = debounce(() => {
    telemetryUpdateCampaignInteractionClickCampaignUpdate(CampaignsTargetContent.EDIT.CHANGE_SPEND, campaignType);
  }, 500);

  return (
    <CampaignFormItem
      data-testid={props['data-testid']}
      title={<FormattedMessage id="campaigns.form.budget.label" />}
      tooltip={{
        description: <FormattedMessage id="campaigns.form.budget.tooltip" />,
      }}
      value={
        <>
          <StyledInputsContainer>
            <Field<DeliveryDiscountFormValues['totalBudgetSum']> name="totalBudgetSum" type="number">
              {field => (
                <RFFFieldErrorWrapper field={field}>
                  {input => {
                    return (
                      <StyledInput
                        {...input}
                        value={input.value ? input.value.toLocaleString(intl.locale, { useGrouping: true }) : ''}
                        onChange={e => {
                          const digitsOnlyStr = e.currentTarget.value.replace(NON_DIGITS_REGEX, '');
                          const numericValue = digitsOnlyStr ? Number(digitsOnlyStr) : UNLIMITED_TOTAL_BUDGET_SUM;
                          handleBudgetChangeTelemetry();
                          input.onChange(numericValue);
                        }}
                        type="string"
                        suffixText={currency}
                        disabled={isDisabled}
                      />
                    );
                  }}
                </RFFFieldErrorWrapper>
              )}
            </Field>

            <StyledCheckboxLabel>
              <InputCheckbox
                name="isUnlimited"
                checked={isUnlimitedBudget}
                onChange={e => handleUnlimitedBudgetCheckbox(e.target.checked)}
                disabled={isDisabled}
              />
              <Typography.Body3>
                <FormattedMessage id="campaigns.form.budget.unlimited-budget" />
              </Typography.Body3>
            </StyledCheckboxLabel>
          </StyledInputsContainer>

          <HelperText text={intl.formatMessage({ id: 'campaigns.form.budget.helper-text' })} />
        </>
      }
    />
  );
};
